import { type ReactNode } from "react";
import { useFeatureFlag } from "@/hooks/useFeatureFlags";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Shield, ArrowRight, Home, MessageSquare, Phone } from "lucide-react";

interface FeatureGateProps {
  flagKey: string;
  userRole?: string;
  children: ReactNode;
  /** If true, show a friendly disabled message instead of hiding */
  showDisabledMessage?: boolean;
  /** Custom message when disabled */
  disabledMessage?: string;
  /** Show admin diagnostics (flag key, etc.) */
  showAdminDiagnostics?: boolean;
}

function UpgradingScreen({
  message,
  flagKey,
  showAdminDiagnostics,
}: {
  message: string;
  flagKey?: string;
  showAdminDiagnostics?: boolean;
}) {
  const navigate = useNavigate();

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-lg text-center space-y-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mx-auto">
          <Shield className="w-8 h-8 text-primary" />
        </div>

        <div className="space-y-3">
          <h1 className="text-2xl font-bold text-foreground">
            This area is not available right now
          </h1>
          <p className="text-muted-foreground leading-relaxed max-w-md mx-auto">
            {message}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Button asChild size="lg" className="gap-2 w-full sm:w-auto">
            <Link to="/quote?intent=callback">
              Request a Call Back <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate("/")}
            className="gap-2 w-full sm:w-auto"
          >
            <Home className="w-4 h-4" /> Back to Home
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
           <a
            href="tel:03330040195"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            Call 0333 004 0195
          </a>
          <a
            href="https://wa.me/447895545248"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            WhatsApp us
          </a>
          <Link
            to="/contact"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Get in touch
          </Link>
        </div>

        {showAdminDiagnostics && flagKey && (
          <div className="mt-6 p-3 rounded-md bg-muted text-xs text-muted-foreground font-mono text-left max-w-xs mx-auto">
            <p className="font-semibold mb-1">Admin diagnostics</p>
            <p>Flag: <code>{flagKey}</code></p>
            <p>Status: disabled</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FeatureGate({
  flagKey,
  userRole,
  children,
  showDisabledMessage = true,
  disabledMessage = "Call or WhatsApp us and we will help you with whatever you need. This section will be available again soon.",
  showAdminDiagnostics = false,
}: FeatureGateProps) {
  const { enabled, isLoading } = useFeatureFlag(flagKey, userRole);

  if (isLoading) return null;

  if (!enabled) {
    return showDisabledMessage ? (
      <UpgradingScreen
        message={disabledMessage}
        flagKey={flagKey}
        showAdminDiagnostics={showAdminDiagnostics}
      />
    ) : null;
  }

  return <>{children}</>;
}
